import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuthContext } from 'context/useAuthContext';
import Logo from '../assets/kitchensync_logo.svg';
import '../styles/components/_authResetPasswordForm.scss';
import { AnalyticsEvents, trackEvent } from 'lib/analytics';
import type { FormEvent } from 'react';

interface ResetPasswordResponse {
  status: string;
  message?: string;
  error?: string;
}

const messages = {
  'auth/expired-action-code': 'The password reset link has expired. Please request a new one.',
  'auth/invalid-action-code': 'The password reset link is invalid. Please request a new one.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/user-not-found': 'No account found with this email.',
  'auth/weak-password': 'Password should be at least 6 characters.',
  'passwords-dont-match': 'Passwords do not match.',
  generic: "That's an error. Please try again.",
} as Record<string, string>;

function AuthResetPasswordForm(): JSX.Element {
  const auth = useAuthContext();
  const navigateTo = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [email, setEmail] = useState<string>('');
  const [oobCode, setOobCode] = useState<string>('');

  // Extract oobCode from URL parameters
  useEffect(() => {
    const code = searchParams.get('oobCode');
    if (code) {
      setOobCode(code);
      verifyResetCode(code);
    } else {
      setErrorMessage('Invalid password reset link. Please request a new one.');
      setLoading(false);
    }
  }, [searchParams]);

  async function verifyResetCode(code: string) {
    try {
      setLoading(true);
      const response = await auth.verifyPasswordResetCode(code);

      if (response.status === 'success' && response.email) {
        setEmail(response.email);
        setLoading(false);
      } else {
        handleError({ code: response.error || 'auth/invalid-action-code' });
        setLoading(false);
      }
    } catch (error: any) {
      handleError({ code: 'auth/invalid-action-code' });
      setLoading(false);
    }
  }

  function handleError(error: any) {
    let message = messages[error.code];
    if (!message) message = messages['generic'];
    setErrorMessage(message);
  }

  function handleOnInput() {
    setErrorMessage('');
    setSuccessMessage('');
    setSubmitted(false);
    return true;
  }

  function validatePasswords(): boolean {
    if (password.length < 6) {
      handleError({ code: 'auth/weak-password' });
      return false;
    }

    if (password !== confirmPassword) {
      handleError({ code: 'passwords-dont-match' });
      return false;
    }

    return true;
  }

  async function handleResetPassword(e: FormEvent) {
    e.preventDefault();
    setSubmitted(true);

    if (!validatePasswords()) {
      setSubmitted(false);
      return;
    }

    try {
      const result = await auth.confirmPasswordReset(oobCode, password);

      if (result.status === 'success') {
        setSuccessMessage('Password reset successful! You can now log in with your new password.');
        trackEvent(AnalyticsEvents.PasswordResetCompleted);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigateTo('/login');
        }, 3000);
      } else {
        handleError({ code: result.error || 'generic' });
        setSubmitted(false);
      }
    } catch (error: any) {
      handleError({ code: 'generic' });
      setSubmitted(false);
    }
  }

  function handleGoToLogin() {
    navigateTo('/login');
  }

  if (loading) {
    return (
      <div className="AuthResetPasswordForm">
        <Logo />
        <div className="ARP-area">
          <div className="loading-message">
            <p>Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (errorMessage && !email) {
    return (
      <div className="AuthResetPasswordForm">
        <Logo />
        <div className="ARP-area">
          <div className="error-state">
            <h2>Invalid Reset Link</h2>
            <p className="text-error">{errorMessage}</p>
            <button className="button w-100" onClick={() => navigateTo('/forgot-password')} type="button">
              Request New Reset Link
            </button>
          </div>
        </div>
        <Link to="/login">Go to Login</Link>
      </div>
    );
  }

  return (
    <div className="AuthResetPasswordForm">
      <Logo />
      <h1 className="visually-hidden">Reset Password</h1>
      <div className="ARP-area">
        {successMessage ? (
          <div className="success-state">
            <h2>Password Reset Successful!</h2>
            <p className="text-success">{successMessage}</p>
            <p>Redirecting to login...</p>
            <button className="button w-100" onClick={handleGoToLogin} type="button">
              Go to Login Now
            </button>
          </div>
        ) : (
          <form className="AuthResetPasswordForm-form" onSubmit={handleResetPassword}>
            <h2>Create New Password</h2>
            <p className="text-center">
              Enter a new password for <strong>{email}</strong>
            </p>

            <label>
              <span className="visually-hidden">New Password</span>
              <input
                placeholder="New Password"
                type="password"
                value={password}
                onInput={e => handleOnInput() && setPassword(e.currentTarget.value)}
                required
                minLength={6}
              />
            </label>

            <label>
              <span className="visually-hidden">Confirm New Password</span>
              <input
                placeholder="Confirm New Password"
                type="password"
                value={confirmPassword}
                onInput={e => handleOnInput() && setConfirmPassword(e.currentTarget.value)}
                required
                minLength={6}
              />
            </label>

            <button className="button w-100" disabled={!password || !confirmPassword || submitted} type="submit">
              {submitted ? 'Updating Password...' : 'Update Password'}
            </button>

            {errorMessage && (
              <label className="d-block text-error text-center" role="alert">
                {errorMessage}
              </label>
            )}

            <div className="password-requirements">
              <p className="text-small text-muted">Password must be at least 6 characters long</p>
            </div>
          </form>
        )}
      </div>

      <Link to="/login">Go to Login</Link>
    </div>
  );
}

export default AuthResetPasswordForm;
